import React, { useState, useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { CNY_PROMO, getCNYPromoEndDate } from '../../../utils/cnyPromo'
import { stripeCheckoutLink } from '../../../redux/actions/myAccountAction'
import { formatNumber, formatPercent } from '../../../i18n/format'
import './cny-promo.css'

// A translation key: the error is translated at render, so it follows a language switch.
const CHECKOUT_ERROR = 'offers:cny.section.error'

// The brand, not copy: it stays the same in every language.
const BRAND_NAME = 'maayot'

// The CNY prices are whole US dollars: '$600', not '$600.00'.
const wholeUsd = (amount: number) =>
  formatNumber(amount, { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })

interface CNYPromoSectionProps {
  proYearlyPriceId: string
}

const CNYPromoSection: React.FC<CNYPromoSectionProps> = ({ proYearlyPriceId }) => {
  const { t } = useTranslation('offers')
  const videoRef = useRef<HTMLVideoElement>(null)
  const [timeLeft, setTimeLeft] = useState(getTimeLeft())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  function getTimeLeft() {
    const diff = getCNYPromoEndDate().getTime() - Date.now()
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 }
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    }
  }

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(getTimeLeft()), 1000)
    return () => clearInterval(timer)
  }, [])

  const handleClaim = async () => {
    if (loading || !proYearlyPriceId) return
    setLoading(true)
    setError('')
    try {
      const affiliateId =
        typeof window !== 'undefined' && (window as any).affiliateId
          ? String((window as any).affiliateId)
          : undefined
      const res: any = await stripeCheckoutLink({
        priceId: proYearlyPriceId,
        couponCode: CNY_PROMO.couponCode,
        ...(affiliateId && { affiliateId }),
      })
      if (res?.return_url) {
        window.open(res.return_url, '_self')
      } else if (res?.url) {
        window.open(res.url, '_self')
      } else {
        setError(CHECKOUT_ERROR)
      }
    } catch {
      setError(CHECKOUT_ERROR)
    } finally {
      setLoading(false)
    }
  }

  const pad = (n: number) => String(n).padStart(2, '0')

  return (
    <div className='cny-promo-hero'>
      {/* Video background */}
      <video
        ref={videoRef}
        className='cny-promo-hero-video'
        autoPlay
        loop
        muted
        playsInline
        preload='auto'
        onCanPlay={() => {
          console.log('CNY video onCanPlay fired')
          videoRef.current?.play().catch((e) => console.error('CNY video play failed:', e))
        }}
        onLoadedData={() => {
          console.log('CNY video onLoadedData fired')
          videoRef.current?.play().catch((e) => console.error('CNY video play failed:', e))
        }}
        onError={(e) => {
          const video = e.currentTarget
          const error = video.error
          console.error('CNY video error:', {
            code: error?.code,
            message: error?.message,
            networkState: video.networkState,
            readyState: video.readyState,
            src: video.currentSrc
          })
        }}
      >
        <source src='/images/background_CNY.mp4' type='video/mp4' />
      </video>
      <div className='cny-promo-hero-overlay' />

      <div className='cny-promo-hero-content'>
        {/* Decorative top */}
        <div className='cny-promo-hero-lanterns'>
          <span>🏮</span>
          <span>🧧</span>
          <span>🏮</span>
        </div>

        <div className='cny-promo-hero-eyebrow'>
          <img src='/images/maayot-text-svg.svg' alt={BRAND_NAME} className='cny-promo-hero-logo' />
        </div>
        <h2 className='cny-promo-hero-heading'>
          <Trans
            t={t}
            i18nKey='cny.section.heading'
            components={{
              line1: <span className='cny-promo-hero-heading-line1' />,
              line2: <span className='cny-promo-hero-heading-line2' />,
            }}
          />
        </h2>

        <p className='cny-promo-hero-wish'>
          <Trans t={t} i18nKey='cny.section.wish' components={{ br: <br /> }} />
        </p>

        {/* Countdown */}
        <div className='cny-promo-hero-countdown'>
          <div className='cny-promo-hero-countdown-unit'>
            <span className='cny-promo-hero-countdown-num'>{pad(timeLeft.days)}</span>
            <span className='cny-promo-hero-countdown-lbl'>{t('promo.countdown.days', { count: timeLeft.days })}</span>
          </div>
          <span className='cny-promo-hero-countdown-sep'>:</span>
          <div className='cny-promo-hero-countdown-unit'>
            <span className='cny-promo-hero-countdown-num'>{pad(timeLeft.hours)}</span>
            <span className='cny-promo-hero-countdown-lbl'>
              {t('promo.countdown.hoursLong', { count: timeLeft.hours })}
            </span>
          </div>
          <span className='cny-promo-hero-countdown-sep'>:</span>
          <div className='cny-promo-hero-countdown-unit'>
            <span className='cny-promo-hero-countdown-num'>{pad(timeLeft.minutes)}</span>
            <span className='cny-promo-hero-countdown-lbl'>
              {t('promo.countdown.minutes', { count: timeLeft.minutes })}
            </span>
          </div>
          <span className='cny-promo-hero-countdown-sep'>:</span>
          <div className='cny-promo-hero-countdown-unit'>
            <span className='cny-promo-hero-countdown-num'>{pad(timeLeft.seconds)}</span>
            <span className='cny-promo-hero-countdown-lbl'>
              {t('promo.countdown.seconds', { count: timeLeft.seconds })}
            </span>
          </div>
        </div>

        {/* Pricing card */}
        <div className='cny-promo-hero-card'>
          <div className='cny-promo-hero-card-badge'>
            {t('cny.section.badge', { percent: formatPercent(CNY_PROMO.discountPercent / 100) })}
          </div>
          <div className='cny-promo-hero-card-plan'>{t('cny.section.plan')}</div>
          <div className='cny-promo-hero-card-tagline'>{t('cny.section.tagline')}</div>
          <div className='cny-promo-hero-card-tagline'>{t('common:teacherLanguageNotice')}</div>
          <div className='cny-promo-hero-card-pricing'>
            <span className='cny-promo-hero-card-old'>
              {t('cny.section.oldPrice', { price: wholeUsd(CNY_PROMO.proMonthlyAnnualized) })}
            </span>
            <span className='cny-promo-hero-card-new'>
              <Trans
                t={t}
                i18nKey='cny.section.newPrice'
                values={{ price: wholeUsd(CNY_PROMO.proYearlyPromoPrice) }}
                components={{ strong: <strong /> }}
              />
            </span>
          </div>
          <div className='cny-promo-hero-card-monthly'>
            {t('cny.section.monthly', { price: wholeUsd(CNY_PROMO.proMonthlyEquivalent) })}
          </div>
          <button
            className='cny-promo-hero-card-cta'
            onClick={handleClaim}
            disabled={loading}
          >
            {loading ? t('promo.processing') : t('cny.section.cta')}
          </button>
          {error && <div className='cny-promo-hero-card-error'>{t(error)}</div>}
          <div className='cny-promo-hero-card-footnote'>{t('cny.section.footnote')}</div>
        </div>
      </div>
    </div>
  )
}

export default CNYPromoSection
